import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Star, StarOff } from 'lucide-react'
import {
  listFacilityContacts,
  createFacilityContact,
  updateFacilityContact,
  deleteFacilityContact,
  type FacilityContact,
} from '../../lib/services/facilityContacts'

export function FacilityContactsPage() {
  const { facilityId } = useParams<{ facilityId: string }>()
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [, setEditId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['facility-contacts', facilityId],
    queryFn: () => listFacilityContacts(facilityId!),
    enabled: !!facilityId,
  })

  const createMutation = useMutation({
    mutationFn: createFacilityContact,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['facility-contacts', facilityId] })
      setShowForm(false)
      setError(null)
    },
    onError: (err: Error) => setError(err.message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ contactId, updates }: { contactId: string; updates: Partial<Omit<FacilityContact, 'phone'>> & { phone?: string } }) =>
      updateFacilityContact(contactId, updates as Parameters<typeof updateFacilityContact>[1]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['facility-contacts', facilityId] })
      setEditId(null)
    },
    onError: (err: Error) => setError(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteFacilityContact,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['facility-contacts', facilityId] }),
    onError: (err: Error) => setError(err.message),
  })

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!facilityId) return
    const fd = new FormData(e.currentTarget)
    createMutation.mutate({
      facility_id: facilityId,
      contact_name: (fd.get('contact_name') as string).trim(),
      email: (fd.get('email') as string).trim(),
      phone: (fd.get('phone') as string).trim() || undefined,
      role_label: (fd.get('role_label') as string).trim() || undefined,
      is_primary: fd.get('is_primary') === 'on',
    })
  }

  if (isLoading) {
    return <div className="p-6 text-sm text-slate-500">Loading contacts…</div>
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800">Facility Contacts</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          <Plus size={16} /> Add Contact
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4"
        >
          <h3 className="mb-3 text-sm font-bold text-slate-700">New Contact</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Name *</label>
              <input
                name="contact_name"
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Jane Smith"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Email *</label>
              <input
                name="email"
                type="email"
                required
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="jane@facility.org"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Phone</label>
              <input
                name="phone"
                type="tel"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="(555) 000-0000"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Role / Title</label>
              <input
                name="role_label"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Administrator"
              />
            </div>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
            <input name="is_primary" type="checkbox" className="rounded" />
            Primary contact (receives report PDFs by default)
          </label>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Saving…' : 'Save Contact'}
            </button>
          </div>
        </form>
      )}

      {contacts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
          No contacts yet. Add a contact to enable PDF delivery.
        </div>
      ) : (
        <ul className="space-y-2">
          {contacts.map((c) => (
            <li
              key={c.contact_id}
              className="flex items-start justify-between rounded-xl border border-slate-200 bg-white p-4"
            >
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-800">{c.contact_name}</span>
                  {c.is_primary && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">
                      Primary
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-sm text-slate-600">{c.email}</div>
                {c.phone && <div className="text-xs text-slate-500">{c.phone}</div>}
                {c.role_label && <div className="text-xs text-slate-500">{c.role_label}</div>}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() =>
                    updateMutation.mutate({
                      contactId: c.contact_id,
                      updates: { is_primary: !c.is_primary },
                    })
                  }
                  title={c.is_primary ? 'Remove primary' : 'Set as primary'}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-amber-500"
                >
                  {c.is_primary ? <Star size={16} className="fill-amber-400 text-amber-400" /> : <StarOff size={16} />}
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Remove ${c.contact_name}?`)) deleteMutation.mutate(c.contact_id)
                  }}
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
